import React, { useState, useEffect, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import emailjs from '@emailjs/browser';
import logo from '../images/logo.png';
import CustomPopup from './CustomPopup';

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID_SIGNUP = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_SIGNUP;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Daily OTP attempt limit
const MAX_OTP_ATTEMPTS_PER_DAY = 5;

const Signup = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    otp: ""
  });
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [verifyClicked, setVerifyClicked] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(MAX_OTP_ATTEMPTS_PER_DAY);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  const debounceTimerRef = useRef(null);
  const emailCheckTimerRef = useRef(null);

  // Redirect if already logged in
  useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (user && token) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const [popupConfig, setPopupConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
    display: 'center'
  });

  const showPopup = (title, message, type = 'success', display = 'center') => {
    setPopupConfig({
      isOpen: true,
      title,
      message,
      type,
      display
    });
  };

  const closePopup = () => {
    setPopupConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Initialize EmailJS
  useEffect(() => {
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init(EMAILJS_PUBLIC_KEY);
    } else {
      console.warn("⚠️ EmailJS public key not found");
    }
  }, []);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
    };
  }, []);

  const checkOtpAttempts = async (email) => {
    try {
      const response = await fetch(`https://chatbot-ai-m3th.onrender.com/otp/attempts/${encodeURIComponent(email)}`);
      const data = await response.json();
      if (response.ok) {
        setRemainingAttempts(data.remaining);
        return data.remaining;
      }
    } catch (error) {
      console.error("Failed to check OTP attempts:", error);
    }
    return 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "username") {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (value.length >= 3) {
        setCheckingUsername(true);
        debounceTimerRef.current = setTimeout(() => {
          checkUsernameAvailability(value);
        }, 500);
      } else {
        setUsernameAvailable(null);
        setCheckingUsername(false);
      }
    }

    if (name === "email") {
      setEmailVerified(false);
      setShowOtpField(false);
      setOtpSent(false);
      setEmailAvailable(null);

      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(value)) {
        setCheckingEmail(true);
        checkOtpAttempts(value);
        emailCheckTimerRef.current = setTimeout(() => {
          checkEmailAvailability(value);
        }, 500);
      }
    }
  };

  const checkUsernameAvailability = async (username) => {
    try {
      const response = await fetch(`https://chatbot-ai-m3th.onrender.com/check-username?username=${encodeURIComponent(username)}`);
      const data = await response.json();

      if (response.ok) {
        setUsernameAvailable(data.available);
      } else {
        setUsernameAvailable(false);
      }
    } catch (error) {
      console.error("Username check failed:", error);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const checkEmailAvailability = async (email) => {
    try {
      const response = await fetch(`https://chatbot-ai-m3th.onrender.com/check-email?email=${encodeURIComponent(email)}`);

      if (response.status === 404) {
        setCheckingEmail(false);
        setEmailAvailable(null);
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setEmailAvailable(data.available);
        if (!data.available) {
          setStatus({
            type: "error",
            message: "Email already registered. Please login instead."
          });
        } else {
          if (status && status.message?.includes("Email already")) {
            setStatus(null);
          }
        }
      }
    } catch (error) {
      console.error("Email check failed:", error);
      setEmailAvailable(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendOtp = async () => {
    if (emailAvailable === false) {
      setStatus({
        type: "error",
        message: "Email already registered. Please login instead."
      });
      return;
    }

    if (remainingAttempts <= 0) {
      setStatus({
        type: "error",
        message: `Daily OTP limit reached. You can only request OTP ${MAX_OTP_ATTEMPTS_PER_DAY} times per day. Please try again tomorrow.`
      });
      return;
    }

    if (!formData.email) {
      setStatus({ type: "error", message: "Please enter your email first" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({ type: "error", message: "Please enter a valid email address" });
      return;
    }

    setVerifyClicked(false);
    setIsSendingOtp(true);
    setStatus(null);

    const otp = generateOTP();
    setGeneratedOtp(otp);
    setFormData(prev => ({ ...prev, otp: "" }));

    try {
      await fetch(`https://chatbot-ai-m3th.onrender.com/otp/increment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });
      await checkOtpAttempts(formData.email);
    } catch (error) {
      console.error("Failed to increment OTP attempts:", error);
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setOtpSent(true);
      setShowOtpField(true);
      setResendTimer(60);
      setIsSendingOtp(false);
      return;
    }

    const templateParams = {
      to_email: formData.email,
      to_name: formData.username || "User",
      username: formData.username || "User",
      email: formData.email,
      otp: otp,
      expiry_time: "10 minutes"
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      setOtpSent(true);
      setShowOtpField(true);
      setResendTimer(60);
      setStatus({
        type: "success",
        message: `OTP sent successfully! (${remainingAttempts - 1} attempts remaining today)`
      });

    } catch (error) {
      console.error("EmailJS error:", error);

      if (error.text?.includes("already") || error.status === 400) {
        setEmailAvailable(false);
        setStatus({
          type: "error",
          message: "Email already registered. Please login instead."
        });
      } else {
        setOtpSent(true);
        setShowOtpField(true);
        setResendTimer(60);
      }

    } finally {
      setIsSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      setStatus({ type: "error", message: "Please enter a valid 6-digit OTP" });
      return;
    }

    setVerifyClicked(true);
    setIsVerifyingOtp(true);
    setStatus(null);

    await new Promise(resolve => setTimeout(resolve, 500));

    if (formData.otp === generatedOtp) {
      setEmailVerified(true);
      setShowOtpField(false);
      setStatus({ type: "success", message: "Email verified successfully!" });
    } else {
      setStatus({ type: "error", message: "Invalid OTP. Please check and try again." });
      setVerifyClicked(false);
    }

    setIsVerifyingOtp(false);
  };

  // 🔥 FIXED: Send signup confirmation email
  const sendSignupConfirmationEmail = async (username, email) => {

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID_SIGNUP || !EMAILJS_PUBLIC_KEY) {
      console.error("❌ EmailJS configuration missing:", {
        service: !!EMAILJS_SERVICE_ID,
        template: !!EMAILJS_TEMPLATE_ID_SIGNUP,
        publicKey: !!EMAILJS_PUBLIC_KEY
      });
      return false;
    }

    const templateParams = {
      to_email: email,
      to_name: username,
      username: username,
      email: email,
      login_link: `${window.location.origin}/login`,
      current_year: new Date().getFullYear()
    };


    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID_SIGNUP,
        templateParams
      );
      return true;
    } catch (error) {
      console.error("❌ Failed to send signup confirmation email:", error);
      return false;
    }
  };

  const handleManualSignup = async (e) => {
    e.preventDefault();

    if (!emailVerified) {
      setStatus({ type: "error", message: "Please verify your email first" });
      return;
    }

    if (!usernameAvailable) {
      setStatus({ type: "error", message: "Please choose an available username" });
      return;
    }

    setStatus(null);
    setIsLoading(true);

    try {
      const res = await fetch("https://chatbot-ai-m3th.onrender.com/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          email_verified: true
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.detail?.includes("Email already") || errorData.detail?.includes("already registered")) {
          throw new Error("Email already registered. Please login instead.");
        }
        throw new Error(errorData.detail || "Signup failed");
      }

      // Send confirmation email
      const emailSent = await sendSignupConfirmationEmail(formData.username, formData.email);

      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      showPopup('Signup Failed', err.message || 'Signup failed', 'error', 'center');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (googleData) => {
    setGoogleLoading(true);

    try {
      // 🔥 STEP 1: Decode Google token to get email FIRST
      const token = googleData.credential;

      // Google token decode (client-side only, no API call needed)
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const userEmail = payload.email;
        const username = payload.name || (userEmail ? userEmail.split('@')[0] : 'User');

        // 🔥 STEP 2: Check if email exists BEFORE calling signup API
        let isNewUser = true;

        if (userEmail) {
          try {
            const checkEmailResponse = await fetch(`https://chatbot-ai-m3th.onrender.com/check-email?email=${encodeURIComponent(userEmail)}`);
            const checkData = await checkEmailResponse.json();

            isNewUser = checkData.available === true;

          } catch (error) {
            console.error("❌ Failed to check email:", error);
          }
        }

        // 🔥 STEP 3: Now call the signup API
        const response = await fetch('https://chatbot-ai-m3th.onrender.com/signup/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: googleData.credential })
        });

        const data = await response.json();

        if (data.status === "success") {
          localStorage.setItem('user', JSON.stringify({
            user_id: data.user_id,
            email: userEmail,
            username: username,
          }));
          localStorage.setItem('token', data.access_token);

          // 🔥 STEP 4: Send welcome email ONLY if it was a NEW user
          if (isNewUser && userEmail) {
            await sendSignupConfirmationEmail(username, userEmail);
          }

          setTimeout(() => navigate('/'), 2000);
        } else {
          throw new Error(data.message || "Google signup failed");
        }
      }
    } catch (error) {
      console.error("❌ Google signup error:", error);
      showPopup('Google Signup Failed', error.message || 'Please try again', 'error', 'center');
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error("❌ Google signup error");
    showPopup('Google Signup Failed', 'Please try again', 'error', 'center');
  };

  const closeStatus = () => {
    setStatus(null);
  };

  const isVerifyButtonDisabled = () => {
    return isSendingOtp ||
      resendTimer > 0 ||
      !formData.email ||
      verifyClicked ||
      emailAvailable === false ||
      remainingAttempts <= 0;
  };

  const getVerifyButtonText = () => {
    if (isSendingOtp) return 'Sending...';
    if (resendTimer > 0) return `${resendTimer}s`;
    if (otpSent) return 'Resend';
    return 'Verify';
  };

  return (
    <>
      {/* Full Screen Google Loading Overlay */}
      {googleLoading && (
        <div style={styles.fullScreenLoadingOverlay}>
          <div style={styles.loadingCard}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3.5rem', height: '3.5rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={styles.loadingText}>Creating your account with Google...</p>
            <p style={styles.loadingSubtext}>Please wait while we set things up</p>
          </div>
        </div>
      )}

      {/* Fixed Navbar Section */}
      <nav className="navbar navbar-expand-lg navbar-dark py-3 fixed-top" style={{ opacity: googleLoading ? 0.5 : 1 }}>
        <div className="container">
          <button
            className="navbar-brand d-flex align-items-center gap-2 btn btn-link text-decoration-none p-0 border-0"
            onClick={() => navigate('/')}
            disabled={googleLoading}
          >
            <img src={logo} alt='Marshall AI Logo' className='logo' />
            <span className="fs-4 fw-bold text-light">Marshall AI</span>
          </button>
          <div className="d-flex gap-3">
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate('/login')}
              disabled={googleLoading}
            >
              Login
            </button>
            <button
              className="btn btn-dark"
              onClick={() => navigate('/signup')}
              disabled={googleLoading}
            >
              Sign Up
            </button>
          </div>
        </div>
      </nav>

      {/* Signup Form Section */}
      <div className="signup-container" style={{ ...styles.container, position: 'relative' }}>

        {/* Manual Signup Loading Overlay */}
        {isLoading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.loadingCardInline}>
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={styles.loadingText}>Creating your account...</p>
            </div>
          </div>
        )}

        <div style={styles.logoContainer}>
          <h2 style={styles.heading}>Create Your Account</h2>
          <p style={styles.subHeading}>Join our community today</p>
        </div>

        <form onSubmit={handleManualSignup} style={styles.form}>
          {/* Username Field */}
          <div style={styles.inputGroup}>
            <label htmlFor="username" style={styles.label}>Username</label>
            <div style={styles.inputWrapper}>
              <input
                id="username"
                name="username"
                placeholder="Choose a username"
                onChange={handleChange}
                value={formData.username}
                required
                style={{
                  ...styles.input,
                  ...(usernameAvailable === true && formData.username.length >= 3 ? styles.inputSuccess : {}),
                  ...(usernameAvailable === false ? styles.inputError : {})
                }}
                autoComplete="off"
                disabled={isLoading || googleLoading}
              />
              {checkingUsername && (
                <span style={styles.inputIcon}>
                  <div style={styles.spinnerSmall}></div>
                </span>
              )}
              {!checkingUsername && usernameAvailable === true && formData.username.length >= 3 && (
                <span style={{ ...styles.inputIcon, color: '#10b981' }}>
                  <i className="bi bi-check-circle-fill"></i>
                </span>
              )}
              {!checkingUsername && usernameAvailable === false && (
                <span style={{ ...styles.inputIcon, color: '#ef4444' }}>
                  <i className="bi bi-x-circle-fill"></i>
                </span>
              )}
            </div>
            {usernameAvailable === true && formData.username.length >= 3 && (
              <small style={styles.successText}>Username is available!</small>
            )}
            {usernameAvailable === false && (
              <small style={styles.errorText}>Username is already taken</small>
            )}
          </div>

          {/* Email Field with OTP */}
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <div style={styles.inputWrapper}>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Enter your email"
                onChange={handleChange}
                value={formData.email}
                required
                style={{
                  ...styles.input,
                  ...(emailVerified ? styles.inputSuccess : {}),
                  ...(emailAvailable === true && !emailVerified ? styles.inputSuccess : {}),
                  ...(emailAvailable === false ? styles.inputError : {})
                }}
                disabled={emailVerified || isLoading || googleLoading}
                autoComplete="off"
              />
              {checkingEmail && (
                <span style={styles.inputIcon}>
                  <div style={styles.spinnerSmall}></div>
                </span>
              )}
              {!emailVerified && !checkingEmail && (
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={isVerifyButtonDisabled() || isLoading || googleLoading}
                  style={{
                    ...styles.otpButton,
                    ...(isVerifyButtonDisabled() ? styles.buttonDisabled : {})
                  }}
                  title={emailAvailable === false ? "Email already registered" :
                    remainingAttempts <= 0 ? "Daily limit reached" : ""}
                >
                  {isSendingOtp ? (
                    <span style={styles.buttonLoading}>
                      <span style={styles.spinnerSmall}></span>
                    </span>
                  ) : (
                    getVerifyButtonText()
                  )}
                </button>
              )}
              {emailVerified && (
                <span style={{ ...styles.inputIcon, color: '#10b981' }}>
                  <i className="bi bi-check-circle-fill"></i>
                </span>
              )}
            </div>

            {!emailVerified && emailAvailable === true && (
              <small style={styles.successText}>Email is available!</small>
            )}
            {!emailVerified && emailAvailable === false && (
              <small style={styles.errorText}>This email is already registered</small>
            )}
            {emailVerified && (
              <small style={styles.successText}>Email verified successfully!</small>
            )}

            {!emailVerified && emailAvailable !== false && remainingAttempts < 3 && remainingAttempts > 0 && (
              <small style={styles.warningText}>
                ⚠️ Only {remainingAttempts} OTP attempt{remainingAttempts !== 1 ? 's' : ''} remaining today
              </small>
            )}
            {!emailVerified && remainingAttempts === 0 && (
              <small style={styles.errorText}>
                ❌ Daily OTP limit reached. Try again tomorrow.
              </small>
            )}
          </div>

          {/* OTP Field */}
          {showOtpField && !emailVerified && (
            <div style={styles.inputGroup}>
              <label htmlFor="otp" style={styles.label}>Enter OTP</label>
              <div style={styles.inputWrapper}>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  onChange={handleChange}
                  value={formData.otp}
                  maxLength="6"
                  style={styles.input}
                  autoComplete="off"
                  disabled={isLoading || googleLoading}
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={isVerifyingOtp || !formData.otp || formData.otp.length !== 6 || verifyClicked || isLoading || googleLoading}
                  style={{
                    ...styles.verifyButton,
                    ...(isVerifyingOtp || verifyClicked ? styles.buttonDisabled : {})
                  }}
                >
                  {isVerifyingOtp ? (
                    <span style={styles.buttonLoading}>
                      <span style={styles.spinnerWhite}></span>
                    </span>
                  ) : verifyClicked ? (
                    'Verifying...'
                  ) : (
                    'Verify OTP'
                  )}
                </button>
              </div>
              <small style={styles.hintText}>
                Didn't receive OTP? Check spam folder or{' '}
                <button
                  type="button"
                  onClick={() => {
                    setVerifyClicked(false);
                    sendOtp();
                  }}
                  disabled={isSendingOtp || resendTimer > 0 || remainingAttempts <= 0 || isLoading || googleLoading}
                  style={{
                    ...styles.resendLink,
                    ...((isSendingOtp || resendTimer > 0 || remainingAttempts <= 0) ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                >
                  resend
                </button>
              </small>
            </div>
          )}

          {/* Password Field with Show/Hide Toggle */}
          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create a password"
                onChange={handleChange}
                value={formData.password}
                required
                style={{
                  ...styles.input,
                  paddingRight: "40px"
                }}
                minLength="6"
                autoComplete="new-password"
                disabled={isLoading || googleLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={isLoading || googleLoading}
              >
                {showPassword ? (
                  <i className="bi bi-eye-slash"></i>
                ) : (
                  <i className="bi bi-eye"></i>
                )}
              </button>
            </div>
            <small style={styles.hintText}>
              Password must be at least 6 characters
            </small>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isLoading || !emailVerified || googleLoading ? styles.buttonDisabled : {})
            }}
            disabled={isLoading || !emailVerified || googleLoading}
          >
            {isLoading ? (
              <span style={styles.buttonLoading}>
                <span style={styles.spinnerWhite}></span>
                Creating...
              </span>
            ) : (
              "Create your account"
            )}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerText}>OR</span>
        </div>

        <div style={styles.socialLogin}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            size="large"
            text="signup_with"
            disabled={isLoading || googleLoading}
          />
        </div>

        {status && (
          <div style={styles.popupOverlay} onClick={closeStatus}>
            <div style={styles.popupCard} onClick={(e) => e.stopPropagation()}>
              <div style={styles.popupIcon}>
                {status.type === "error" ? (
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444" }}></i>
                ) : (
                  <i className="bi bi-check-circle-fill" style={{ color: "#10b981" }}></i>
                )}
              </div>
              <h3 style={styles.popupTitle}>
                {status.type === "error" ? "Error!" : "Success!"}
              </h3>
              <p style={styles.popupMessage}>
                {status.message}
              </p>
              <button
                style={{
                  ...styles.popupButton,
                  backgroundColor: status.type === "error" ? "#ef4444" : "#10b981"
                }}
                onClick={closeStatus}
              >
                {status.type === "error" ? "Close" : "Got it"}
              </button>
            </div>
          </div>
        )}

        <p style={styles.loginLink}>
          Already have an account?{' '}
          <a
            href="/login"
            style={styles.link}
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
          >
            Log in
          </a>
        </p>
      </div>

      {/* Custom Popup Component */}
      <CustomPopup
        isOpen={popupConfig.isOpen}
        onClose={closePopup}
        title={popupConfig.title}
        message={popupConfig.message}
        type={popupConfig.type}
        display={popupConfig.display}
      />

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
            display: inline-block;
          }
        `}
      </style>
    </>
  );
};

const styles = {
  warningText: {
    color: "#f59e0b",
    fontSize: "0.75rem",
    marginTop: "4px",
    display: "block",
  },
  container: {
    maxWidth: "450px",
    margin: "auto",
    padding: "2.5rem",
    borderRadius: "12px",
    marginTop: "100px",
    marginBottom: "40px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    backgroundColor: "#ffffff",
  },
  fullScreenLoadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    zIndex: 10
  },
  loadingCard: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    minWidth: '300px',
  },
  loadingCardInline: {
    textAlign: 'center',
    padding: '20px'
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '18px',
    color: '#1e293b',
    fontWeight: '600',
  },
  loadingSubtext: {
    marginTop: '8px',
    fontSize: '14px',
    color: '#64748b',
  },
  logoContainer: {
    marginBottom: "2rem",
  },
  heading: {
    fontSize: "1.8rem",
    fontWeight: "600",
    color: "#2d3748",
    marginBottom: "0.5rem",
  },
  subHeading: {
    fontSize: "0.95rem",
    color: "#718096",
    marginBottom: "0",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  inputGroup: {
    textAlign: "left",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontSize: "0.9rem",
    color: "#4a5568",
    fontWeight: "500",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  input: {
    width: "100%",
    padding: "0.8rem 1rem",
    fontSize: "0.95rem",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s",
    boxSizing: "border-box",
  },
  inputSuccess: {
    borderColor: "#10b981",
    backgroundColor: "#f0fdf4",
  },
  inputError: {
    borderColor: "#ef4444",
    backgroundColor: "#fef2f2",
  },
  inputIcon: {
    position: "absolute",
    right: "12px",
    fontSize: "1.1rem",
  },
  passwordToggle: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: "1.2rem",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "color 0.2s",
    outline: "none",
  },
  otpButton: {
    position: "absolute",
    right: "8px",
    padding: "0.4rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: "500",
    color: "#3b82f6",
    backgroundColor: "transparent",
    border: "1px solid #3b82f6",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
    minWidth: "60px",
  },
  verifyButton: {
    position: "absolute",
    right: "8px",
    padding: "0.4rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: "500",
    color: "#ffffff",
    backgroundColor: "#3b82f6",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
    minWidth: "80px",
  },
  buttonDisabled: {
    opacity: "0.7",
    cursor: "not-allowed",
  },
  buttonLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  spinnerSmall: {
    width: "16px",
    height: "16px",
    border: "2px solid #3b82f6",
    borderTopColor: "transparent",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },
  spinnerWhite: {
    width: "16px",
    height: "16px",
    border: "2px solid #ffffff",
    borderTopColor: "transparent",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },
  successText: {
    color: "#10b981",
    fontSize: "0.75rem",
    marginTop: "4px",
    display: "block",
  },
  errorText: {
    color: "#ef4444",
    fontSize: "0.75rem",
    marginTop: "4px",
    display: "block",
  },
  hintText: {
    color: "#718096",
    fontSize: "0.75rem",
    marginTop: "4px",
    display: "block",
  },
  resendLink: {
    background: "none",
    border: "none",
    color: "#3b82f6",
    textDecoration: "underline",
    cursor: "pointer",
    padding: "0",
    fontSize: "0.75rem",
  },
  button: {
    padding: "0.9rem",
    background: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
    marginTop: "0.5rem",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    margin: "1.5rem 0",
    color: "#cbd5e0",
  },
  dividerText: {
    flex: 1,
    position: "relative",
    fontSize: "0.85rem",
    color: "#718096",
  },
  socialLogin: {
    marginBottom: "1.5rem",
    display: "flex",
    justifyContent: "center",
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  popupCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "32px",
    maxWidth: "400px",
    width: "90%",
    textAlign: "center",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
  },
  popupIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  popupTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "12px",
  },
  popupMessage: {
    fontSize: "14px",
    color: "#64748b",
    marginBottom: "24px",
    lineHeight: "1.5",
  },
  popupButton: {
    backgroundColor: "#ef4444",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  loginLink: {
    fontSize: "0.95rem",
    color: "#4a5568",
  },
  link: {
    color: "#3b82f6",
    fontWeight: "500",
    textDecoration: "none",
    cursor: "pointer",
  },
};

export default Signup;